import type { IncidentResponse, UpdateIncidentRequest } from "./api";

/**
 * Editing an incident's own fields on the detail page (`incident.edit`,
 * [F-001]): title, description, category, severity and areas - the fields the
 * operation covers, and only those. Owner, Bearbeiter and tags are operations
 * of their own and keep their own controls.
 */
export interface IncidentEditDraft {
  title: string;
  description: string;
  categoryId: string;
  severityId: string;
  areaIds: string[];
}

/** Mirrors the api's UpdateIncidentRequest bound. */
export const MAX_TITLE_CHARS = 200;

export function draftFromIncident(incident: IncidentResponse): IncidentEditDraft {
  return {
    title: incident.title,
    description: incident.description,
    categoryId: incident.categoryId,
    severityId: incident.severityId,
    areaIds: [...incident.areaIds],
  };
}

export type IncidentEditProblem = "titleRequired" | "titleTooLong" | "descriptionRequired" | "areasRequired";

/** What stops the draft from being saved; empty when it may be. */
export function editProblems(draft: IncidentEditDraft): IncidentEditProblem[] {
  const out: IncidentEditProblem[] = [];
  const title = draft.title.trim();
  if (title === "") out.push("titleRequired");
  if (title.length > MAX_TITLE_CHARS) out.push("titleTooLong");
  if (draft.description.trim() === "") out.push("descriptionRequired");
  // DO-001: at least one area.
  if (draft.areaIds.length === 0) out.push("areasRequired");
  return out;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

/**
 * The PATCH body: only what changed, so the audit entry names exactly the
 * edited fields. Null when nothing changed - there is nothing to send.
 */
export function buildIncidentPatch(
  incident: IncidentResponse,
  draft: IncidentEditDraft,
): UpdateIncidentRequest | null {
  const body: UpdateIncidentRequest = {};
  const title = draft.title.trim();
  const description = draft.description.trim();
  if (title !== incident.title) body.title = title;
  if (description !== incident.description) body.description = description;
  if (draft.categoryId !== incident.categoryId) body.categoryId = draft.categoryId;
  if (draft.severityId !== incident.severityId) body.severityId = draft.severityId;
  if (!sameSet(draft.areaIds, incident.areaIds)) body.areaIds = [...draft.areaIds];
  return Object.keys(body).length > 0 ? body : null;
}

/**
 * The entries a picker offers: the active ones, plus whatever the incident
 * already carries even if deactivated since - [F-009] keeps it on existing
 * incidents, so the edit must be able to show it and leave it in place.
 */
export function selectableEntries<T extends { id: string; active: boolean }>(
  list: readonly T[] | undefined,
  carried: readonly string[],
): T[] {
  return (list ?? []).filter((entry) => entry.active || carried.includes(entry.id));
}
